'use client';

/**
 * ConnectionCard Component
 *
 * Displays a single organization connection with its health status,
 * linked user info, and management actions.
 */

import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  Inline,
  Stack,
  Text,
} from '@cortex/ui';
import { CheckCircle, Github, User, XCircle } from 'lucide-react';
import * as React from 'react';

import { API_BASE_URL, useFetchWithAuth } from '@/hooks/use-api';

import { ConnectionHealthBadge } from './ConnectionHealthBadge';

interface ConnectionCardProps {
  /** Connection data to display */
  connection: {
    id: string;
    label: string;
    providerId: string;
    providerType: string;
    metadata: {
      github_account?: {
        login: string;
        avatar_url: string;
        type: 'User' | 'Organization';
      };
      repository_selection?: 'all' | 'selected';
      installation_id?: string;
      installed_at?: string;
      github_user?: {
        login: string;
        avatar_url: string;
      };
      expires_at?: string;
      refresh_expires_at?: string;
    };
    validationStatus: string | null;
    linkedProjectCount?: number;
  };
  /** Organization ID for reconnect flow */
  organizationId: string;
  /** Callback when user clicks Manage button */
  onManage: (id: string) => void;
  /** Callback when user clicks Disconnect button */
  onDisconnect: (id: string) => void;
}

function formatTimeUntilExpiry(expiresAt?: string): {
  text: string;
  isUrgent: boolean;
} {
  if (!expiresAt) return { text: '', isUrgent: false };

  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();

  if (diff < 0) return { text: 'Expired', isUrgent: true };

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return {
      text: `Expires in ${days} day${days === 1 ? '' : 's'}`,
      isUrgent: days <= 3,
    };
  }

  return {
    text: `Expires in ${hours} hour${hours === 1 ? '' : 's'}`,
    isUrgent: true,
  };
}

function getProviderDisplayName(providerId: string): string {
  switch (providerId.toLowerCase()) {
    case 'github':
      return 'GitHub';
    default:
      return providerId;
  }
}

function getProviderIcon(providerId: string): React.ReactNode {
  switch (providerId.toLowerCase()) {
    case 'github':
      return <Github className="h-5 w-5" />;
    default:
      return <Stack className="bg-muted-foreground/40 h-5 w-5 rounded" />;
  }
}

export function ConnectionCard({
  connection,
  organizationId,
  onManage,
  onDisconnect,
}: ConnectionCardProps) {
  const fetchWithAuth = useFetchWithAuth();
  const { id, label, providerId, metadata, validationStatus } = connection;

  const [testStatus, setTestStatus] = React.useState<
    'idle' | 'testing' | 'success' | 'error'
  >('idle');
  const [testMessage, setTestMessage] = React.useState<string | null>(null);

  const handleReconnect = () => {
    const reconnectUrl = `${API_BASE_URL}/admin/connections/${providerId}/authorize?organizationId=${organizationId}&label=${encodeURIComponent(label)}`;
    window.location.href = reconnectUrl;
  };

  const handleTest = async () => {
    setTestStatus('testing');
    setTestMessage(null);

    try {
      const response = await fetchWithAuth(`/admin/connections/${id}/test`, {
        method: 'POST',
      });

      const result = await response.json();
      const data = result.data || result;

      if (data.success) {
        setTestStatus('success');
        setTestMessage(data.message || 'Connection is working');
      } else {
        setTestStatus('error');
        setTestMessage(data.error || 'Connection test failed');
      }
    } catch {
      setTestStatus('error');
      setTestMessage('Failed to test connection');
    }

    setTimeout(() => {
      setTestStatus('idle');
      setTestMessage(null);
    }, 5000);
  };

  const linkedCount = connection.linkedProjectCount ?? 0;
  const providerName = getProviderDisplayName(providerId);
  const providerIcon = getProviderIcon(providerId);

  const githubAccount = metadata.github_account || metadata.github_user;
  const isAppInstallation = !!metadata.github_account;

  const expiryInfo = isAppInstallation
    ? { text: '', isUrgent: false }
    : formatTimeUntilExpiry(metadata.expires_at);

  const connectedAccount = githubAccount
    ? {
        name:
          metadata.github_account?.type === 'Organization'
            ? githubAccount.login
            : `@${githubAccount.login}`,
        avatarUrl: githubAccount.avatar_url,
        extra: metadata.repository_selection
          ? metadata.repository_selection === 'all'
            ? 'all repos'
            : 'selected repos'
          : null,
      }
    : null;

  const accessibleLabel = `${providerName} connection: ${label}`;

  return (
    <Card
      aria-label={accessibleLabel}
      role="article"
      className="hover:bg-secondary relative p-5 transition-all duration-150 hover:border-white/12 hover:shadow-md"
    >
      {/* Header */}
      <Inline className="mb-4" align="start" justify="between" gap="md">
        <Inline gap="md">
          <Stack
            className="bg-surface-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            aria-hidden="true"
          >
            {providerIcon}
          </Stack>
          <Stack gap="none" className="min-w-0">
            <Text weight="semibold" className="truncate">
              {label}
            </Text>
            <Text size="sm" color="muted" className="truncate">
              {providerName}
            </Text>
          </Stack>
        </Inline>
        <ConnectionHealthBadge
          isAppInstallation={isAppInstallation}
          expiresAt={metadata.expires_at ?? undefined}
          refreshExpiresAt={metadata.refresh_expires_at ?? undefined}
          validationStatus={validationStatus}
        />
      </Inline>

      {/* Connected Account */}
      {connectedAccount && (
        <Inline gap="sm" className="mb-4">
          <Avatar className="h-7 w-7 shrink-0">
            <AvatarImage
              src={connectedAccount.avatarUrl}
              alt={connectedAccount.name}
            />
            <AvatarFallback>
              <User className="text-foreground/60 h-3.5 w-3.5" />
            </AvatarFallback>
          </Avatar>
          <Text size="sm" color="muted" className="truncate">
            <Text as="span" className="sr-only">
              Connected to{' '}
            </Text>
            {connectedAccount.name}
            {connectedAccount.extra && (
              <Text as="span" size="xs" className="ml-1">
                ({connectedAccount.extra})
              </Text>
            )}
          </Text>
        </Inline>
      )}

      {/* Metadata */}
      <Inline wrap className="mb-5 gap-x-5 gap-y-2" justify="between">
        {expiryInfo.text && (
          <Text
            size="sm"
            color={expiryInfo.isUrgent ? 'error' : 'muted'}
            role={expiryInfo.isUrgent ? 'alert' : undefined}
          >
            {expiryInfo.text}
          </Text>
        )}
        {isAppInstallation && (
          <Text size="sm" color="success">
            App installed
          </Text>
        )}
        <Text size="sm" color="muted">
          <Text as="span" className="sr-only">
            Linked to{' '}
          </Text>
          {linkedCount} project{linkedCount === 1 ? '' : 's'}
        </Text>
      </Inline>

      {/* Test Result */}
      {testMessage && (
        <Alert
          variant={testStatus === 'success' ? 'success' : 'destructive'}
          className="mb-4"
        >
          {testStatus === 'success' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <AlertDescription>{testMessage}</AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <Inline gap="sm">
        <Button
          variant="outline"
          size="sm"
          onClick={handleTest}
          loading={testStatus === 'testing'}
          aria-label={`Test ${label} connection`}
        >
          {testStatus === 'testing' ? 'Testing' : 'Test'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onManage(id)}
          aria-label={`Manage ${label} connection`}
        >
          Manage
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleReconnect}
          aria-label={`Reconnect ${label}`}
        >
          Reconnect
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onDisconnect(id)}
          aria-label={`Disconnect ${label}`}
        >
          Disconnect
        </Button>
      </Inline>
    </Card>
  );
}
